import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // In dev/mocked SMTP, the token is returned for convenience
      if (res.data.reset_token) setResetToken(res.data.reset_token);
      toast.success("Reset instructions sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F0E] flex items-center justify-center p-4">
      <SEO title="Forgot Password" description="Reset your BCH account password" path="/forgot-password" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/bch-logo.png" alt="BCH" className="h-20 mx-auto mb-4" />
          </Link>
        </div>

        <div className="bg-[#111413] border border-white/10 rounded-2xl p-8">
          {!sent ? (
            <>
              <h1 className="text-white text-xl font-bold mb-2">Forgot Password?</h1>
              <p className="text-white/50 text-sm mb-6">Enter your email and we'll send you a link to reset your password.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="email" placeholder="Enter your email"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white pl-10 h-12"
                    data-testid="forgot-email-input" required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-semibold" data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-[#2A9D8F] mx-auto mb-4" weight="fill" />
              <h2 className="text-white text-xl font-bold mb-2">Check Your Email</h2>
              <p className="text-white/50 text-sm mb-6">
                If an account exists with <span className="text-white font-medium">{email}</span>, you'll receive a password reset link shortly.
              </p>
              {resetToken && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6 text-left">
                  <p className="text-yellow-400 text-xs font-semibold mb-2 uppercase tracking-wider">Dev Mode — Reset Link</p>
                  <Link to={`/reset-password?token=${resetToken}`} className="text-[#2A9D8F] text-sm break-all hover:underline" data-testid="dev-reset-link">
                    Click here to reset password
                  </Link>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-white/50 text-sm hover:text-white flex items-center justify-center gap-1.5" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
